import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SearchIcon from "@mui/icons-material/Search";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import React, { useMemo } from "react";
import { useSession } from "users/__session/session-context";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetUserNetworkConnectionsQuery } from "users/networking/connections/_api/user-network-connection-api";
import UserInformation from "users/information/user-information";
import { useGetEntityMembersByEntityIdQuery } from "_shared/entity-members/_api/entity-member-api";
import { skipToken } from "@reduxjs/toolkit/query";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";

const PopoverContainer = styled("div")(({ theme }) => ({
  minWidth: "22rem",
  padding: theme.spacing(2),
  textAlign: "center",
}));
const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    paddingLeft: theme.spacing(1)
  },
  width: "100%",
})) as typeof TextField;
const LoaderProgress = styled(CircularProgress)(({ theme }) => ({
  display: "inline-block",
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(4),
}));
const ListContainer = styled(List)(() => ({
  maxHeight: "20rem",
  overflowX: "hidden",
  overflowY: "auto",
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

type ClientSelectorProps = {
  clientUserId?: Guid;
  popoverAnchor?: HTMLElement;
  onClientUpdated?: (userInfo?: UserInformation) => void;
  onPopoverClose?: () => void;
};

export default function ClientSelector(props: Readonly<ClientSelectorProps>) {
  const { clientUserId, popoverAnchor, onClientUpdated, onPopoverClose } = props;

  const session = useSession();

  const [searchTerm, setSearchTerm] = React.useState<string>("");

  const networkConnectionQuery = useGetUserNetworkConnectionsQuery();
  const entityMemberQuery = useGetEntityMembersByEntityIdQuery(
    session.user?.legalEntityId.value ? { legalEntityId: session.user?.legalEntityId.value } : skipToken,
    { skip: Boolean(session.context?.viewingAsVendor) }
  );
  const userIds = useMemo(() => {
    if (!networkConnectionQuery.data && !entityMemberQuery.data) {
      return new Set<string>();
    }
    const idsFromConnections = networkConnectionQuery.data
      ?.map((nc) => nc.id)
      .filter(id => !isNullOrUndefined(id)) ?? [];
    const idsFromEntityMembers = entityMemberQuery.data?.map((em) => em.userId) ?? [];
    return new Set([...idsFromConnections, ...idsFromEntityMembers]);
  }, [networkConnectionQuery.data, entityMemberQuery.data]);

  const individualsQuery = useGetIndividualsByUserIdsQuery(
    userIds.size ? { userIds } : skipToken
  );

  const usersInfo = useMemo(() => {
    if (!individualsQuery.data) return [];
    const individuals = Individual.fromJSONArray(individualsQuery.data) ?? [];
    return individuals.map(ind => ind.toUserInformation());
  }, [individualsQuery.data]);

  const selectedClient = useMemo(() => {
    if (usersInfo.length === 0 || !clientUserId) return undefined;
    return usersInfo.find((c) => c.id?.isEqualTo(clientUserId));
  }, [clientUserId, usersInfo]);

  const clientFilterResults = useMemo(() => {
    if (searchTerm === "") return usersInfo;
    return usersInfo.filter((r) =>
      r.name.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.email?.value.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, usersInfo]);

  function search(value: string) {
    setSearchTerm(value);

  }

  function saveChanges(client?: UserInformation) {
    onClientUpdated?.(client);
    onPopoverClose?.();
  }

  return (
    <Popover
      anchorEl={popoverAnchor}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={popoverAnchor !== undefined}
      onClose={() => onPopoverClose?.()}
    >
      <PopoverContainer>
        <SearchField
          placeholder="Client Representatives"
          slotProps={{
            input: {
              startAdornment: <SearchIcon />,
              endAdornment: (
                <>
                  {searchTerm.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm("");
                        search("");
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </>
              ),
            },
          }}
          value={searchTerm}
          onChange={(event) => search(event.target.value)}
        />
        {networkConnectionQuery.isLoading && <LoaderProgress size={50} disableShrink />}
        {!networkConnectionQuery.isLoading && (
          <ListContainer dense>
            {clientFilterResults.length < 1 && (
              <ListItem>
                <ListItemText
                  primary="None Found"
                  slotProps={{ primary: { sx: { textAlign: "center", padding: 1, typography: "body1" } } }}
                />
              </ListItem>
            )}
            {clientFilterResults.length > 0 &&
              clientFilterResults.map((client: UserInformation) => {
                const isSelected = selectedClient?.id?.isEqualTo(client.id);

                return (
                  <ListItemButton
                    disableGutters
                    key={client.id?.value}
                    selected={isSelected}
                    onClick={() => {
                      if (!client.id || !client.legalEntityId) {
                        throw new Error("Client or legal Entity ID is missing");
                      }
                      saveChanges(client);
                    }}
                  >
                    <ListItemAvatar>
                      {isSelected ? (
                        <SelectedIcon>
                          <DoneIcon />
                        </SelectedIcon>
                      ) : (
                        <IndividualAvatar
                          individual={client.toIndividual()}
                        />
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={client.name?.fullName}
                      secondary={<>{client.email?.value}</>}
                    />
                  </ListItemButton>
                );
              })}
          </ListContainer>
        )}
      </PopoverContainer>
    </Popover>
  );
}
